import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import PropTypes from 'prop-types'
import { useSelector } from 'hooks/redux'
import { equals, isEmpty, path } from 'ramda'
import {
  REFERENCE_PARAMETER,
  SUPPORT_PARAMETER,
  FEATURE,
  ADD_FEATURE,
  FEATURE_COLLECTIONS,
  VEHICLES,
  FAST_FEATURES,
} from 'utils/constants'
import { getIsDrawerVisible } from 'selectors/drawer'
import {
  getSearchStringByCollection,
  getSearchResults,
  getSearchMetaByCollection,
  getIsSearching,
  getIsSearchSubmitted,
  getFiltersByCollection,
  getSearchByBusFilterByCollection,
} from 'selectors/search'
import {
  startSearch,
  endSearch,
  setFilters,
  handlePaginateSearch,
  handleSubmitSearch,
  submitSearchStart,
  setBusFilterFlag,
  resetCollectionSearchResultsAndInitialState,
  setSearchString,
  clearFilterTerms,
  clearAllDropdowns,
  resetChipFilters,
} from 'actions/search'
import { options as SWITCH_NUMBER_OPTIONS_FULL } from 'components/pages/FastFeatures/FastFeatures'
import { Loading, Header, Col, SelectMenu, Divider } from 'components/patterns'
import { classNames, capitalizeEachWord } from 'utils/utils'
import featuresStrings from 'utils/strings/features-strings'

import AdvancedRPSearchFilters from 'components/AdvancedRPSearchFilters'
import AdvancedSearchFilters from 'components/AdvancedSearchFilters'
import AdvancedFeaturesSearchFilters from 'components/AdvancedFeaturesSearchFilters'
import {
  CreateIcon,
  PreCreateFeatureButton,
  ResultsHeaderWrapper,
} from 'components/pages/FeatureSearch/FeatureSearch.style'
import FeaturePreCreateModal from 'components/pages/FeatureSearch/FeaturePreCreateModal'
import { yup } from 'schemas/validation'
import SearchInput from './SearchInput'
import withStyle, {
  SearchForm,
  SearchFormRow,
  AdvancedSearchWrap,
  StyledPagedTable,
  StyledCheckbox,
  SearchBoxChipFilterCol,
  AdvancedSearchArea,
  AdvancedSearchCol,
  AdvancedChipsCol,
  ResultTableRow,
  ResultsTable,
  AdvancedSearchCard,
  SearchBoxChipFilterCard,
  StyledBusCheckboxFilter,
  FastFeaturesFiltersRow,
  FastFeaturesFilterArea,
  SwitchFilterMenu,
  SwitchCurrentMenu,
} from './AdvancedSearch.style'

const SWITCH_NUMBER_OPTIONS = SWITCH_NUMBER_OPTIONS_FULL.filter(
  (option) => option.key !== 'switch-all'
)
const DEFAULT_RESULTS_TOTAL = -1
const SEARCH_INPUT_WIDTH = '295'
const SEARCH_INPUT_WIDTH_ADD_PARAM = '400'
const SEARCH_INPUT_WIDTH_ADD_PARAM_ADD_FEATURE = '370'
const CURRENT_OPTIONS = featuresStrings.current_options.map((cur) => ({
  key: cur,
  value: cur,
  title: cur,
}))

/* eslint-disable react-hooks/exhaustive-deps */
const AdvancedSearch = ({
  className,
  columns,
  collectionName,
  searchUri,
  hasSidebar,
  SearchTips,
  forcedResults,
  alwaysShowResults,
  enableCreateFeature,
  onCreateFeatureClick,
  showPreCreateFeatureModal,
  togglePreCreateFeatureModal,
  canOpenPreCreateFeatureModal,
  handleClearFilters,
  handleFilterOnChange,
  hideChips,
  labelText,
  hideSecondaryFilterIfEmpty,
  serialNumber,
  isHorizontalLayout,
  addToSearchRequestBody,
  collectionContext,
  onSearch,
  onClear,
  searchTableTitle,
  overrideOnPageChangeHandler,
  isOfflineSearch,
  noResultsText,
  hidePagination,
  searchHeaderChildren,
  labelType,
  beforeTableContent,
  afterTableContent,
  searchResults: overrideSearchResults,
  onCompatibilityFilterChange,
  searchTerm,
  filterModules,
  initialSearchQuery,
}) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const activeFilters = useSelector(getFiltersByCollection, collectionName)
  const [page, setPage] = useState(1)
  const [appliedFilters, setAppliedFilters] = useState({})
  const [showCompatibleResults, setCompatibilityFilter] = useState(true)
  const [showBusFilter, setShowBusFilter] = useState(false)
  // Tracks a pending URL-seeded search query (two-phase pattern: phase 1 sets
  // this state which triggers a re-render; phase 2 fires handleSubmitSearch
  // after the saga has had a full render cycle to set up its listeners).
  const [pendingUrlSearch, setPendingUrlSearch] = useState(null)
  const [switchNumber, setSwitchNumber] = useState(activeFilters?.switchNumber)
  const [current, setCurrent] = useState(activeFilters?.current)
  const [solenoid, setSolenoid] = useState(null)
  const [shouldUpdateFastFeaturesFilters, setShouldUpdateFastFeaturesFilters] =
    useState(false)

  const { startIndex, totalResults } = useSelector(
    getSearchMetaByCollection,
    collectionName
  )

  const isDrawerVisible = useSelector(getIsDrawerVisible)
  const searchString = useSelector(getSearchStringByCollection, collectionName)
  const reduxBusFilterFlag = useSelector(
    getSearchByBusFilterByCollection,
    collectionName
  )

  const searchResultsFromStore = useSelector(getSearchResults, collectionName)
  const isSearching = useSelector(getIsSearching)
  const isSearchSubmitted = useSelector(getIsSearchSubmitted)
  const results = (() => {
    if (totalResults === DEFAULT_RESULTS_TOTAL) {
      return forcedResults
    }

    if (overrideSearchResults !== undefined) {
      return overrideSearchResults
    }

    return searchResultsFromStore
  })()

  const searchTableTotalResults =
    totalResults !== DEFAULT_RESULTS_TOTAL ? totalResults : NaN
  const searchTableHeader =
    totalResults !== DEFAULT_RESULTS_TOTAL
      ? searchString || searchTerm
        ? `${featuresStrings.search_results} for "${(
            searchString || searchTerm
          ).replace(/['"]+/g, '')}"`
        : featuresStrings.search_results
      : capitalizeEachWord(searchTableTitle || featuresStrings.recently_viewed)

  const searchInputTooltip =
    SearchTips !== null
      ? SearchTips || featuresStrings.vehicle_search_explainer
      : null

  const handleOnPageChange = useCallback(
    (pageData) => {
      if (typeof overrideOnPageChangeHandler !== 'function') {
        dispatch(
          handlePaginateSearch(pageData, collectionName, addToSearchRequestBody)
        )
      } else {
        overrideOnPageChangeHandler(pageData)
      }
    },
    [dispatch, handlePaginateSearch, collectionName, addToSearchRequestBody]
  )

  const handleSetFilters = useCallback(
    (values) => dispatch(setFilters(values, collectionName)),
    [dispatch, setFilters, collectionName]
  )

  const startSearchCallback = useCallback(() => {
    if (!isOfflineSearch) {
      // When addToSearchRequestBody is provided (e.g. collection context with varMinCompat),
      // store it flat in Redux so pagination requests pick up the correct fields.
      // The nested { serialNumber, addToSearchRequestBody } wrapper is only needed when
      // there is no explicit addToSearchRequestBody (e.g. plain feature search).
      const bodyToStore = addToSearchRequestBody || {
        serialNumber,
        addToSearchRequestBody,
      }
      dispatch(startSearch(collectionName, searchUri, bodyToStore))
    }
  }, [
    dispatch,
    startSearch,
    collectionName,
    searchUri,
    addToSearchRequestBody,
    serialNumber,
  ])

  const endSearchCallback = useCallback(
    () => dispatch(endSearch()),
    [dispatch, endSearch]
  )

  const handleCheckbox = (e) => {
    const isChecked = path(['target', 'checked'], e)
    setCompatibilityFilter(isChecked)
    if (typeof onCompatibilityFilterChange === 'function') {
      onCompatibilityFilterChange(isChecked)
    }
    if (serialNumber) {
      // Vehicle context: re-submit search with filterConflicts toggled so the
      // server returns only compatible results and pagination works correctly.
      const updatedSearchBody = {
        ...addToSearchRequestBody,
        serial: serialNumber,
        filterConflicts: isChecked,
      }
      dispatch(startSearch(collectionName, searchUri, updatedSearchBody))
      dispatch(submitSearchStart(true))
      dispatch(handleSubmitSearch(collectionName, true))
    } else if (collectionContext?.varMinCompat) {
      // Collection context: rebuild the body based on the stable collectionContext prop,
      // which always carries varMinCompat/mdlDbCd/buildDate/vehicleSubType regardless of
      // the current checkbox state (addToSearchRequestBody is stripped when checkbox is OFF
      // so we can't destructure from it here).
      //
      // Checkbox ON: send full collection context — backend routes to
      //   SearchFeaturesForCollection for variant compatibility filtering + VPDM.
      // Checkbox OFF: send only moduleNames (ECU restriction) — backend routes to
      //   SearchFeatures, producing results identical to the standalone Features page.
      const { varMinCompat, mdlDbCd, buildDate, vehicleSubType } =
        collectionContext
      // moduleNames comes from addToSearchRequestBody — both ON and OFF bodies always
      // carry it (ALLOWED_COLLECTION_ECUS_NORMALIZED). collectionContext does not contain
      // moduleNames, so no fallback to collectionContext is needed or valid.
      const moduleNames = addToSearchRequestBody?.moduleNames
      const updatedSearchBody = isChecked
        ? {
            varMinCompat,
            filterConflicts: true,
            mdlDbCd,
            buildDate,
            vehicleSubType,
            moduleNames,
            statuses: ['active'],
          }
        : {
            // Checkbox OFF: only ECU restriction; no collection-specific routing fields.
            moduleNames,
          }
      dispatch(startSearch(collectionName, searchUri, updatedSearchBody))
      dispatch(submitSearchStart(true))
      dispatch(handleSubmitSearch(collectionName, true))
    }
  }

  const handleBusFilterClick = (e) => {
    const busFilterFlag = path(['target', 'checked'], e)
    setShowBusFilter(busFilterFlag)
    dispatch(setBusFilterFlag(collectionName, busFilterFlag))
    dispatch(submitSearchStart(true))
    dispatch(handleSubmitSearch(collectionName, true))
  }

  const handleDropdownOnChange = (val, setter) => {
    setter(val)
    setShouldUpdateFastFeaturesFilters(true)
  }

  const handleSolenoidOnChange = (value) => {
    setSolenoid(value)
    setShouldUpdateFastFeaturesFilters(true)
  }

  // Start/stop the search saga — runs once on mount/unmount only.
  // Phase 1: start the saga and seed the search string.  We do NOT dispatch
  // handleSubmitSearch here — setting pendingUrlSearch triggers a re-render,
  // and the phase-2 effect below fires handleSubmitSearch only after the saga
  // has had a full render cycle to reach its take(ADS_HANDLE_SUBMIT_SEARCH).
  useEffect(() => {
    if (hasSidebar) {
      startSearchCallback()
      if (initialSearchQuery) {
        dispatch(setSearchString(initialSearchQuery, collectionName))
        setPendingUrlSearch(initialSearchQuery)
      }
    }

    const isSearchOnAddFeature = collectionName === FEATURE && isDrawerVisible
    return () => {
      if (!isSearchOnAddFeature) endSearchCallback()
    }
  }, [])

  // Phase 2: fires after the re-render caused by setPendingUrlSearch above.
  // By this point the search saga is guaranteed to be listening for
  // ADS_HANDLE_SUBMIT_SEARCH, so the dispatch is reliably received.
  useEffect(() => {
    if (pendingUrlSearch) {
      dispatch(handleSubmitSearch(collectionName, true))
    }
  }, [pendingUrlSearch])

  // Re-run the fresh-navigation reset (or return-from-detail re-submit) whenever
  // the pathname changes. Using pathname (not location.key) means filter
  // interactions that don't change the URL never trigger this effect.
  // useLocation() is used (not history.location) because useHistory() returns a
  // stable reference that does not trigger re-renders on navigation.
  useEffect(() => {
    // Only reset when opening the standalone Features page (not modal or drawer)
    const isStandaloneFeaturePage =
      collectionName === FEATURE && !isHorizontalLayout && !isDrawerVisible
    // Returning via the "< Feature Search" back button preserves filters and
    // re-runs the search instead of showing Recently Viewed
    const isReturningFromDetail = location.state?.fromDetail === true

    if (isStandaloneFeaturePage && !isReturningFromDetail) {
      // Full reset: clear results, search string, filter terms, dropdown
      // selections, chip filters, and bus filter flag so the page opens in a
      // clean state (busFilterFlag persists in Redux across navigations but
      // the checkbox local state resets to false on mount, so we must sync them)
      dispatch(resetCollectionSearchResultsAndInitialState(collectionName))
      dispatch(setSearchString('', collectionName))
      dispatch(clearFilterTerms(collectionName))
      dispatch(clearAllDropdowns(collectionName))
      dispatch(resetChipFilters({}, collectionName))
      dispatch(setBusFilterFlag(collectionName, false))
    }

    // Re-submit the search if returning from a detail page with active filters
    if (
      isStandaloneFeaturePage &&
      isReturningFromDetail &&
      (searchString || !isEmpty(activeFilters))
    ) {
      // Sync the bus filter checkbox with persisted Redux state so the UI
      // matches what will actually be sent in the re-submitted search request
      setShowBusFilter(reduxBusFilterFlag)
      dispatch(submitSearchStart(true))
      dispatch(handleSubmitSearch(collectionName, true))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  useEffect(() => {
    // eslint-disable-next-line no-unused-expressions
    startIndex === 1 && setPage(1)
  }, [startIndex])

  useEffect(() => {
    if (shouldUpdateFastFeaturesFilters) {
      dispatch(
        setFilters(
          {
            switchNumber: switchNumber === 'all' ? '' : switchNumber,
            current,
            solenoid,
          },
          FAST_FEATURES
        )
      )
      dispatch(submitSearchStart(true))
      dispatch(
        handleSubmitSearch(collectionName, true, true, addToSearchRequestBody)
      )
      setShouldUpdateFastFeaturesFilters(false)
    }
  }, [shouldUpdateFastFeaturesFilters])

  const isFeatures = equals(collectionName, FEATURE)
  const isFeatureCollections = equals(collectionName, FEATURE_COLLECTIONS)
  const isFastFeatures = equals(collectionName, FAST_FEATURES)
  const searchInputWidth =
    isFeatures && isHorizontalLayout
      ? SEARCH_INPUT_WIDTH_ADD_PARAM_ADD_FEATURE
      : isFastFeatures
      ? SEARCH_INPUT_WIDTH_ADD_PARAM
      : SEARCH_INPUT_WIDTH

  const renderPropsForFilter = {
    setFilters: handleSetFilters,
    hideChips,
    onChange: handleFilterOnChange,
    handleClearFilters,
    collectionName,
    searchString,
    isSearching,
    hideSecondaryFilterIfEmpty,
  }

  const SearchInputBox = (
    <SearchInput
      collectionName={collectionName}
      searchUri={searchUri}
      page={page}
      setPage={setPage}
      filters={appliedFilters}
      addToSearchRequestBody={addToSearchRequestBody}
      tooltip={isHorizontalLayout ? null : searchInputTooltip}
      label={labelText}
      labelType={labelType}
      width={searchInputWidth}
      onSearch={onSearch}
      onClear={onClear}
      fetchResults={!isOfflineSearch}
    />
  )

  const CompatibleFeaturesCheckbox = (
    <StyledCheckbox checked={showCompatibleResults} onChange={handleCheckbox}>
      {featuresStrings.only_show_compatible_features}
    </StyledCheckbox>
  )

  const BusFeatureFilter = (
    <StyledBusCheckboxFilter
      checked={showBusFilter}
      onChange={handleBusFilterClick}
    >
      {featuresStrings.school_bus_filter}
    </StyledBusCheckboxFilter>
  )

  const SwitchNumberDropdown = (
    <SwitchFilterMenu>
      <SelectMenu
        label={featuresStrings.switch_number}
        className="switch-number-filter-menu"
        name="switch-type"
        onChange={(val) => handleDropdownOnChange(val, setSwitchNumber)}
        options={SWITCH_NUMBER_OPTIONS}
        value={switchNumber}
        rules={yup.string().nullable()}
        placeholder={featuresStrings.select_criteria}
      />
    </SwitchFilterMenu>
  )

  const CurrentAmpDropdown = (
    <SwitchCurrentMenu>
      <SelectMenu
        label={featuresStrings.current_a}
        name="current"
        className="current-filter-menu"
        onChange={(val) => handleDropdownOnChange(val, setCurrent)}
        options={CURRENT_OPTIONS}
        value={current}
        rules={yup.string().nullable()}
        placeholder={featuresStrings.select_criteria}
      />
    </SwitchCurrentMenu>
  )

  const hasSearchInput = isSearchSubmitted && !isEmpty(searchString)
  const hasFilters = !isEmpty(activeFilters)

  // Don't show main results if we're overriding with empty array (all unauthorized)
  const hasOverrideWithEmptyResults =
    overrideSearchResults !== undefined &&
    overrideSearchResults.length === 0 &&
    searchResultsFromStore.length > 0

  let isSearchComplete

  if (forcedResults?.length || alwaysShowResults) {
    isSearchComplete = !isSearching || hasSearchInput || hasFilters
  } else {
    isSearchComplete = !isSearching && (hasSearchInput || hasFilters)
  }

  // Hide main results table if all results are unauthorized
  if (hasOverrideWithEmptyResults) {
    isSearchComplete = false
  }

  const pagedTableClass = isHorizontalLayout
    ? classNames(className, 'add-feature-search')
    : classNames(className, 'new-search')
  const ResultsPagedTable = isSearching ? (
    <Loading contained={true} size="sm" />
  ) : (
    (isSearchComplete || hasOverrideWithEmptyResults) && (
      <>
        <ResultsTable hasSidebar={hasSidebar}>
          <ResultsHeaderWrapper>
            <Header type="h3">{searchTableHeader}</Header>
            {enableCreateFeature && (
              <span>
                <PreCreateFeatureButton
                  type="secondary"
                  onClick={() => history.push('/fast-features')}
                  disabled={!enableCreateFeature}
                >
                  {featuresStrings.ff_view_fast_features}
                </PreCreateFeatureButton>
                <PreCreateFeatureButton
                  type="primary"
                  className="create-feature"
                  onClick={onCreateFeatureClick}
                  disabled={!enableCreateFeature}
                >
                  <>
                    <CreateIcon icon="add" fill="white" />
                    {featuresStrings.create_feature}
                  </>
                </PreCreateFeatureButton>
              </span>
            )}
            {canOpenPreCreateFeatureModal && (
              <FeaturePreCreateModal
                className="pre-create-feature-modal"
                modalIsOpen={showPreCreateFeatureModal}
                toggleModal={togglePreCreateFeatureModal}
              />
            )}
            {searchHeaderChildren}
          </ResultsHeaderWrapper>
          {beforeTableContent}
          {!hasOverrideWithEmptyResults && (
            <StyledPagedTable
              className={pagedTableClass}
              columns={columns}
              isSearching={isSearching}
              dataSource={results}
              resource={collectionName}
              startIndex={startIndex}
              totalResults={searchTableTotalResults}
              onPageChange={handleOnPageChange}
              noResultText={noResultsText}
              hidePagination={hidePagination}
            />
          )}
          {afterTableContent}
        </ResultsTable>
      </>
    )
  )

  const AddFeaturesResultsPagedTable = isSearching ? (
    <Loading contained={true} size="sm" />
  ) : (
    isSearchComplete && (
      <>
        <Header type="h3">{searchTableHeader}</Header>
        <StyledPagedTable
          className={pagedTableClass}
          columns={columns}
          isSearching={isSearching}
          dataSource={results}
          resource={collectionName}
          startIndex={startIndex}
          totalResults={searchTableTotalResults}
          onPageChange={handleOnPageChange}
        />
      </>
    )
  )

  const renderStrategy = {
    [REFERENCE_PARAMETER]: () => (
      <AdvancedRPSearchFilters key={collectionName} />
    ),
    [SUPPORT_PARAMETER]: (renderProps) => (
      <AdvancedSearchFilters {...renderProps} key={collectionName} />
    ),
    [VEHICLES]: (renderProps) => null,
    [FEATURE]: () => (
      <AdvancedFeaturesSearchFilters
        render={(ChipFilters, AdvancedFilters, AdvancedFilterChips) => (
          <div className={className}>
            <SearchForm hasSidebar={hasSidebar}>
              {SearchInputBox}
              {BusFeatureFilter}
              <AdvancedSearchArea>
                <AdvancedSearchWrap>
                  {ChipFilters}
                  {AdvancedFilters}
                  {AdvancedFilterChips}
                </AdvancedSearchWrap>
              </AdvancedSearchArea>
            </SearchForm>
            {ResultsPagedTable}
          </div>
        )}
        key={collectionName}
        searchString={searchString}
        isSearching={isSearching}
        applyFilters={(filters) => setAppliedFilters(filters)}
        isHorizontalLayout={isHorizontalLayout}
        filterModules={filterModules}
      />
    ),
    [FEATURE_COLLECTIONS]: () => (
      <AdvancedFeaturesSearchFilters
        searchCollectionName={collectionName}
        render={(ChipFilters) => (
          <div className={className}>
            <SearchForm hasSidebar={hasSidebar}>
              {SearchInputBox}
              <AdvancedSearchArea>
                <AdvancedSearchWrap>{ChipFilters}</AdvancedSearchWrap>
              </AdvancedSearchArea>
            </SearchForm>
            {ResultsPagedTable}
          </div>
        )}
        key={collectionName}
        searchString={searchString}
        isSearching={isSearching}
        applyFilters={(filters) => setAppliedFilters(filters)}
        isHorizontalLayout={isHorizontalLayout}
      />
    ),
    [ADD_FEATURE]: () => (
      <AdvancedFeaturesSearchFilters
        render={(ChipFilters, AdvancedFilters, AdvancedFilterChips) => (
          <>
            <div className={classNames(className, 'add-feature-search')}>
              <SearchFormRow align="top">
                <SearchBoxChipFilterCol>
                  <SearchBoxChipFilterCard>
                    {SearchInputBox}
                    {CompatibleFeaturesCheckbox}
                    {ChipFilters}
                  </SearchBoxChipFilterCard>
                </SearchBoxChipFilterCol>
                <AdvancedSearchCol>
                  <AdvancedSearchCard>{AdvancedFilters}</AdvancedSearchCard>
                </AdvancedSearchCol>
                <AdvancedChipsCol>{AdvancedFilterChips}</AdvancedChipsCol>
              </SearchFormRow>
            </div>
            <ResultTableRow>
              <Col span={24}>{AddFeaturesResultsPagedTable}</Col>
            </ResultTableRow>
          </>
        )}
        key={collectionName}
        searchString={searchString}
        isSearching={isSearching}
        applyFilters={(filters) => setAppliedFilters(filters)}
        isHorizontalLayout={isHorizontalLayout}
        filterModules={filterModules}
      />
    ),
    [FAST_FEATURES]: () => (
      <AdvancedFeaturesSearchFilters
        render={(
          ChipFilters,
          AdvancedFilters,
          AdvancedFilterChips,
          SolenoidFilter
        ) => (
          <div className={className}>
            <FastFeaturesFilterArea>
              <FastFeaturesFiltersRow>
                {SearchInputBox}
                <Divider type="vertical" />
                {SwitchNumberDropdown}
                <Divider type="vertical" />
                {CurrentAmpDropdown}
                <Divider type="vertical" />
                {SolenoidFilter}
              </FastFeaturesFiltersRow>
              {ChipFilters}
            </FastFeaturesFilterArea>
            {ResultsPagedTable}
          </div>
        )}
        key={collectionName}
        searchString={searchString}
        isSearching={isSearching}
        applyFilters={(filters) => setAppliedFilters(filters)}
        isHorizontalLayout={isHorizontalLayout}
        onSolenoidChange={handleSolenoidOnChange}
        isFastFeatures
        addToSearchRequestBody={addToSearchRequestBody}
      />
    ),
  }

  const renderFilters = useMemo(
    () => renderStrategy[collectionName](renderPropsForFilter),
    [collectionName, renderPropsForFilter]
  )

  if (isHorizontalLayout && isFeatures) {
    return renderStrategy[ADD_FEATURE]()
  }

  if (isFeatures) {
    return renderStrategy[FEATURE]()
  }

  if (isFeatureCollections) {
    return renderStrategy[FEATURE_COLLECTIONS]()
  }

  if (isFastFeatures) {
    return renderStrategy[FAST_FEATURES]()
  }

  return (
    <div className={className}>
      <SearchForm hasSidebar={hasSidebar}>
        {SearchInputBox}
        {renderFilters}
      </SearchForm>
      {ResultsPagedTable}
    </div>
  )
}

AdvancedSearch.defaultProps = {
  labelType: featuresStrings.header_text,
}

AdvancedSearch.propTypes = {
  SearchTips: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.element,
    PropTypes.func,
  ]),
  className: PropTypes.string,
  searchTableTitle: PropTypes.string,
  searchUri: PropTypes.string,
  columns: PropTypes.array,
  enableCreateFeature: PropTypes.bool,
  togglePreCreateFeatureModal: PropTypes.func,
  showPreCreateFeatureModal: PropTypes.bool,
  canOpenPreCreateFeatureModal: PropTypes.bool,
  onCreateFeatureClick: PropTypes.func,
  handleClearFilters: PropTypes.func,
  handleFilterOnChange: PropTypes.func,
  hideChips: PropTypes.bool,
  collectionName: PropTypes.string,
  isSearching: PropTypes.bool,
  isOfflineSearch: PropTypes.bool,
  addToSearchRequestBody: PropTypes.object,
  collectionContext: PropTypes.shape({
    varMinCompat: PropTypes.string,
    mdlDbCd: PropTypes.string,
    buildDate: PropTypes.string,
    vehicleSubType: PropTypes.string,
  }),
  hasSidebar: PropTypes.bool,
  forcedResults: PropTypes.array,
  labelText: PropTypes.string,
  hideSecondaryFilterIfEmpty: PropTypes.bool,
  isHorizontalLayout: PropTypes.bool,
  hidePagination: PropTypes.bool,
  serialNumber: PropTypes.string,
  onSearch: PropTypes.func,
  overrideOnPageChangeHandler: PropTypes.func,
  onClear: PropTypes.func,
  noResultsText: PropTypes.string,
  searchHeaderChildren: PropTypes.node,
  labelType: PropTypes.string,
  beforeTableContent: PropTypes.node,
  afterTableContent: PropTypes.node,
  searchResults: PropTypes.array,
  onCompatibilityFilterChange: PropTypes.func,
  alwaysShowResults: PropTypes.bool,
  searchTerm: PropTypes.string,
  filterModules: PropTypes.arrayOf(PropTypes.string),
  initialSearchQuery: PropTypes.string,
}

export default withStyle(AdvancedSearch)
